import { useEffect } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { SEO_DATA, SITE_URL, buildLocalBusinessJsonLd } from "@/i18n/seo";

const HREFLANG_MAP = {
  cs: { hreflang: "cs", url: `${SITE_URL}/?lang=cs` },
  uk: { hreflang: "uk", url: `${SITE_URL}/?lang=uk` },
  en: { hreflang: "en", url: `${SITE_URL}/?lang=en` },
};

const upsertMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
    return el;
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
};

const upsertLink = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
};

const setJsonLd = (id, data) => {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

export default function SEO() {
  const { lang } = useI18n();
  const data = SEO_DATA[lang] || SEO_DATA.cs;

  useEffect(() => {
    document.documentElement.lang = data.lang;
    document.title = data.title;

    upsertMeta('meta[name="description"]', {
      name: "description",
      content: data.description,
    });
    upsertMeta('meta[name="keywords"]', {
      name: "keywords",
      content: data.keywords,
    });
    upsertMeta('meta[name="robots"]', {
      name: "robots",
      content: "index, follow, max-image-preview:large",
    });
    upsertMeta('meta[name="author"]', {
      name: "author",
      content: "DBR Invest a.s.",
    });

    // Open Graph
    upsertMeta('meta[property="og:type"]', {
      property: "og:type",
      content: "website",
    });
    upsertMeta('meta[property="og:site_name"]', {
      property: "og:site_name",
      content: "DBR Invest a.s.",
    });
    upsertMeta('meta[property="og:title"]', {
      property: "og:title",
      content: data.og_title,
    });
    upsertMeta('meta[property="og:description"]', {
      property: "og:description",
      content: data.og_description,
    });
    upsertMeta('meta[property="og:url"]', {
      property: "og:url",
      content: SITE_URL,
    });
    upsertMeta('meta[property="og:image"]', {
      property: "og:image",
      content: `${SITE_URL}/logo-dbr-invest.png`,
    });
    upsertMeta('meta[property="og:locale"]', {
      property: "og:locale",
      content: data.og_locale,
    });
    // Alternate locales for OG
    document.head
      .querySelectorAll('meta[property="og:locale:alternate"]')
      .forEach((n) => n.remove());
    Object.entries(SEO_DATA)
      .filter(([k]) => k !== lang)
      .forEach(([, v]) => {
        const m = document.createElement("meta");
        m.setAttribute("property", "og:locale:alternate");
        m.setAttribute("content", v.og_locale);
        document.head.appendChild(m);
      });

    // Twitter
    upsertMeta('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: "summary_large_image",
    });
    upsertMeta('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: data.og_title,
    });
    upsertMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: data.og_description,
    });
    upsertMeta('meta[name="twitter:image"]', {
      name: "twitter:image",
      content: `${SITE_URL}/logo-dbr-invest.png`,
    });

    // Canonical + hreflang
    upsertLink('link[rel="canonical"]', {
      rel: "canonical",
      href: SITE_URL,
    });
    document.head
      .querySelectorAll('link[rel="alternate"][hreflang]')
      .forEach((n) => n.remove());
    Object.values(HREFLANG_MAP).forEach((entry) => {
      const link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hreflang", entry.hreflang);
      link.setAttribute("href", entry.url);
      document.head.appendChild(link);
    });
    const xDefault = document.createElement("link");
    xDefault.setAttribute("rel", "alternate");
    xDefault.setAttribute("hreflang", "x-default");
    xDefault.setAttribute("href", `${SITE_URL}/`);
    document.head.appendChild(xDefault);

    // JSON-LD
    setJsonLd("ld-business", buildLocalBusinessJsonLd(lang));
    setJsonLd("ld-website", {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${SITE_URL}#website`,
      url: SITE_URL,
      name: "DBR Invest a.s.",
      inLanguage: data.lang,
      publisher: { "@id": `${SITE_URL}#business` },
    });
  }, [lang, data]);

  return null;
}
